/**
 * ConnectModal — 420px. idle (method picker + oauth/key form) →
 * authorizing (spinner) → done (check + version). Verbatim copy + flow.
 */
import React from 'react';
import { Pressable, StyleSheet, Text, TextInput, View } from 'react-native';
import { colors, fontFamily, fontSize, fontWeight, radii } from '../theme';
import { Button, Modal, Spinner } from '../components';
import { Check } from '../icons';
import { useBridge } from '../store/bridgeStore';
import { allInts } from '../domain/catalog';
import { enabledMethods, recommendedMethod, METHOD_LABEL } from '../domain/auth';
import type { AuthMethod } from '../domain/types';

export default function ConnectModal() {
  const { state, actions } = useBridge();
  if (!state.connectId) return null;
  const ints = allInts(state.adapters, state.metaOverrides);
  const c = ints.find(i => i.id === state.connectId);
  if (!c) return null;

  const cfg = state.authCfg[c.id];
  const supported = enabledMethods(cfg, c);
  const recommended = recommendedMethod(cfg, c);
  const method = state.connectMethod;
  const step = state.connectStep;
  const isOauth = method === 'oauth';
  const isKeyAuth = method === 'apikey' || method === 'bearer';

  return (
    <Modal width={420} zIndex={60} onClose={actions.closeConnect}>
      <View style={styles.body}>
        <View style={[styles.avatar, { backgroundColor: c.color } as any]}>
          <Text style={styles.avatarText}>{c.initials}</Text>
        </View>

        {step === 'idle' && (
          <>
            <Text style={styles.title}>{'Connect ' + c.name}</Text>

            {supported.length > 1 && (
              <View style={{ marginBottom: 16, width: '100%' }}>
                <Text style={styles.methodLabel}>CONNECTION METHOD</Text>
                <View style={styles.methodSeg}>
                  {supported.map(m => (
                    <Pressable key={m} onPress={() => actions.setConnectMethod(m as AuthMethod)} style={[styles.methodOpt, m === method ? styles.methodOptActive : null]}>
                      <Text style={[styles.methodOptText, m === method ? styles.methodOptActiveText : null]}>{METHOD_LABEL[m]}</Text>
                    </Pressable>
                  ))}
                </View>
                {method === recommended && <Text style={styles.recommendedNote}>{'Recommended by ' + c.name + '.'}</Text>}
              </View>
            )}

            {isOauth && (
              <>
                <Text style={styles.note}>{"You'll be redirected to " + c.name + ' to authorize access. SK Bridge never sees your password.'}</Text>
                <Button label={'Authorize with ' + c.name} onPress={() => actions.authorize(c.id)} height={40} />
              </>
            )}

            {isKeyAuth && (
              <>
                <Text style={styles.note}>{c.name + ' uses ' + (method === 'bearer' ? 'a bearer token' : 'an API key') + '. Paste your credential — SK Bridge stores it by reference in the vault.'}</Text>
                <View style={{ width: '100%', marginBottom: 14 }}>
                  {method === 'apikey' && (
                    <>
                      <Text style={styles.fieldLabel}>Header name</Text>
                      <View style={styles.headerBox}><Text style={styles.headerBoxText}>{(cfg && cfg.headerName) || 'X-API-Key'}</Text></View>
                    </>
                  )}
                  <Text style={styles.fieldLabel}>{method === 'bearer' ? 'Bearer token' : 'API key'}</Text>
                  <TextInput
                    value={state.connectKey}
                    onChangeText={actions.setConnectKey}
                    secureTextEntry
                    placeholder={method === 'bearer' ? 'paste token' : 'paste API key'}
                    placeholderTextColor={colors.ink4 as any}
                    style={styles.input}
                  />
                </View>
                <Button label={'Connect ' + c.name} onPress={() => actions.submitConnectKey(c.id)} height={40} />
              </>
            )}
          </>
        )}

        {step === 'authorizing' && (
          <>
            <Text style={styles.title}>{isOauth ? 'Redirecting…' : 'Verifying…'}</Text>
            <Text style={styles.note}>{isOauth ? 'Completing the OAuth handoff.' : 'Checking the credential with ' + c.name + '.'}</Text>
            <Spinner size={24} />
          </>
        )}

        {step === 'done' && (
          <>
            <View style={styles.doneCircle}><Check size={22} color={colors.success} /></View>
            <Text style={styles.title}>Connected</Text>
            <Text style={styles.note}>You're on vendor version</Text>
            <Text style={styles.version}>{c.versions[0]}</Text>
            <Button label="Choose what can use it" onPress={() => actions.finishConnect(c.id)} height={40} />
          </>
        )}
      </View>

      <View style={styles.footer}>
        <Pressable onPress={actions.closeConnect}><Text style={styles.cancel}>Cancel</Text></Pressable>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  body: { padding: 24, alignItems: 'center' },
  avatar: { width: 56, height: 56, borderRadius: 14, alignItems: 'center', justifyContent: 'center', marginBottom: 16 },
  avatarText: { fontFamily: fontFamily.sans, fontWeight: fontWeight.semibold, fontSize: fontSize.xl, color: '#fff' },
  title: { fontFamily: fontFamily.sans, fontWeight: fontWeight.semibold, fontSize: fontSize.xl, color: colors.ink, marginBottom: 6 },
  note: { fontFamily: fontFamily.sans, color: colors.ink3, fontSize: fontSize.md, lineHeight: 19.5, marginBottom: 20, textAlign: 'center' },
  methodLabel: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink3, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6, textAlign: 'left' },
  methodSeg: { flexDirection: 'row', gap: 4, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line, borderRadius: radii.r2, padding: 3 },
  methodOpt: { flex: 1, borderRadius: radii.r1, paddingVertical: 7, paddingHorizontal: 4, alignItems: 'center', cursor: 'pointer' } as any,
  methodOptActive: { backgroundColor: colors.ink },
  methodOptText: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, fontWeight: fontWeight.medium, color: colors.ink3 },
  methodOptActiveText: { color: colors.paper as any },
  recommendedNote: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink3, marginTop: 6, textAlign: 'left' },
  fieldLabel: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, color: colors.ink2, fontWeight: fontWeight.medium, marginBottom: 4 },
  headerBox: { borderWidth: 1, borderColor: colors.line, borderRadius: radii.r2, backgroundColor: colors.paper2, paddingVertical: 9, paddingHorizontal: 11, marginBottom: 12 },
  headerBoxText: { fontFamily: fontFamily.mono, fontSize: fontSize.sm, color: colors.ink2 },
  input: { width: '100%', height: 38, paddingHorizontal: 11, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.card, borderRadius: radii.r2, fontFamily: fontFamily.mono, fontSize: fontSize.sm, color: colors.ink as any, outlineStyle: 'none' } as any,
  doneCircle: { width: 44, height: 44, borderRadius: 22, backgroundColor: colors.successSoft, alignItems: 'center', justifyContent: 'center', marginTop: -8, marginBottom: 12 },
  version: { fontFamily: fontFamily.mono, fontSize: fontSize.lg, color: colors.ink, marginBottom: 20 },
  footer: { paddingVertical: 12, paddingHorizontal: 18, borderTopWidth: 1, borderTopColor: colors.line, backgroundColor: colors.paper2, alignItems: 'center' },
  cancel: { fontFamily: fontFamily.sans, color: colors.ink3, fontSize: fontSize.sm, cursor: 'pointer' } as any,
});
